import { Box, Paper, Typography } from "@material-ui/core";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import LineChart from "src/components/ScoringChart/Line/LineChart";
import { beautifulTooltip } from "src/components/ScoringChart/tool-utils/BeautifulTooltip";
import DateGroupButton, { DATE_OPTION } from "src/components/ScoringChart/tool-utils/DateGroupButton";
import MultipleSelector from "src/components/Selector/MultipleSelector";
import { NoteTypography } from "src/components/Typography/TitleTypography";
import DetailMonitoringWrapper from "src/components/Wrapper/DetailMonitoringWrapper";
import { MONITORING } from "src/config/constants";
import {
  useAnalysisOverviewChartSelector,
  useAnalysisOverviewSelector,
} from "src/context/monitoring/analysisOverviewContext";
import { useParticularPoolsSelector } from "src/context/poolsContext";
import { numberWithCommas, toFixed } from "src/services/utils";

const getOption = () => {
  return {
    chart: {
      height: 500,
    },
    legend: {
      enabled: true,
    },
    ...beautifulTooltip((value) => `${numberWithCommas(toFixed(value * 100, 2))}%`),
    xAxis: {
      gridLineWidth: 1,
      type: "datetime",
      labels: {
        enabled: true,
        format: "{value:%e.%b}",
      },
    },
  };
};

export default function TokenUtilizationRate() {
  const { pool } = useParams();
  const { fetchUtilizationRate } = useAnalysisOverviewSelector((state) => state.fetch);
  const { utilizationRateLoading } = useAnalysisOverviewSelector((state) => state.loading);
  const utilizationRate = useAnalysisOverviewChartSelector(MONITORING.OVERVIEW.utilizationRate, pool);
  const token = utilizationRate?.metadata?.tokens ?? "";

  const [selectedData, setSelectedData] = useState([]);
  const { token: tokenConfig, fetch } = useParticularPoolsSelector(pool);

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    if (utilizationRate?.active === undefined)
      fetchUtilizationRate(pool, { number: 360, interval: 2, token: "" }, { active: 2 });
    else fetchUtilizationRate(pool, utilizationRate?.metadata, { active: utilizationRate?.active });
  }, [utilizationRate?.active, pool]);

  useEffect(() => {
    let result = [];
    if (tokenConfig && utilizationRate?.data) {
      let _tokenAddresses = Object.keys(utilizationRate.data);
      for (var address of _tokenAddresses) {
        const _tokenConfig = tokenConfig[address];
        result.push({
          label: _tokenConfig["symbol"],
          address: address,
        });
      }
    }
    setSelectedData(result);
  }, [tokenConfig, utilizationRate]);

  const { series } = useMemo(() => {
    if (tokenConfig && utilizationRate?.data && !utilizationRateLoading) {
      let _series = [];
      for (var token in utilizationRate.data) {
        let name = tokenConfig[token].symbol;
        _series.push({
          name: name,
          data: utilizationRate["data"][token].map((element) => {
            return [].concat(element);
          }),
        });
      }
      return { series: _series };
    }
    return { series: [] };
  }, [tokenConfig, utilizationRate, utilizationRateLoading]);

  const tokenData = useMemo(() => {
    if (tokenConfig) {
      return Object.entries(tokenConfig).map((element) => {
        return {
          label: <Typography>{element[1]["symbol"]}</Typography>,
          address: element[0],
        };
      });
    } else return [];
  }, [tokenConfig, pool]);

  function onChooseItem(event, item, selectedItems) {
    let _tokens = "";
    for (const _item of selectedItems) _tokens += `${_item["address"]},`;
    const _tokenData = _tokens.slice(0, -1);
    let tokens = "";
    if (_tokenData.length > 0) tokens = _tokens.slice(0, -1);
    let _params = utilizationRate?.metadata ? { ...utilizationRate.metadata } : {};
    _params["token"] = tokens;
    fetchUtilizationRate(pool, _params, { active: utilizationRate?.active });
  }

  function onRemoveItem(event, item, selectedItems) {
    let _tokens = "";
    for (const _item of selectedItems) _tokens += `${_item["address"]},`;
    const _tokenData = _tokens.slice(0, -1);
    let tokens = "";
    if (_tokenData.length > 0) tokens = _tokens.slice(0, -1);
    let _params = utilizationRate?.metadata ? { ...utilizationRate.metadata } : {};
    _params["token"] = tokens;
    fetchUtilizationRate(pool, _params, { active: utilizationRate?.active });
  }

  return (
    <DetailMonitoringWrapper
      navigation={{ text: "overview", name: "token utilization rate", link: `/overview-analysis/${pool}` }}
      selectLink="/overview-analysis/token-utilization-rate"
      Component={{
        Description: (
          <NoteTypography>
            The chart shows the token utilization rate in the selected lending pool over time. The utilization rate is
            recorded every hour. Users can select the tokens they are interested in.
          </NoteTypography>
        ),
      }}
    >
      <Paper>
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <DateGroupButton
            defaultActive={utilizationRate?.active ?? 0}
            picker={{
              continueClick: (from, to, active) => {
                fetchUtilizationRate(pool, { from, to, token: token }, { active });
              },
            }}
            config={DATE_OPTION.map((item, _) => ({
              data: item,
              onClick: (data, active) => {
                fetchUtilizationRate(pool, { number: data.number, interval: data.interval, token: token }, { active });
              },
            }))}
          />
        </Box>
        <Box mb={2}>
          <MultipleSelector
            mb={2}
            items={tokenData}
            defaultSelectedItems={selectedData}
            event={{
              onChooseItem: (event, item, selectedItems) => onChooseItem(event, item, selectedItems),
              onRemoveItem: (event, item, selectedItems) => onRemoveItem(event, item, selectedItems),
            }}
            config={{ maxItem: 5 }}
          />
        </Box>
        <LineChart isLoading={utilizationRateLoading} series={series} option={getOption()} />
      </Paper>
    </DetailMonitoringWrapper>
  );
}
